import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '@root/firebase'; // Adjust the import as per your project structure
import dayjs from 'dayjs';

export async function fetchCategories() {
  try {
    const today = dayjs().startOf('day');

    // Fetch categories
    const categorySnapshot = await getDocs(
      collection(db, 'marketplaceCategories')
    );
    const categories = categorySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      activeCount: 0,
      futureCount: 0,
      productCount: 0,
    }));

    // Fetch all adverts in a single request
    const advertSnapshot = await getDocs(collection(db, 'adverts'));
    const adverts = advertSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Process each category asynchronously
    const categoryPromises = categories.map(async (category) => {
      // Get products count for this category
      const productQuery = query(
        collection(db, 'products'),
        where('subCategoryId', '==', category.id)
      );
      const productSnapshot = await getDocs(productQuery);
      const productCount = productSnapshot.size;

      // Filter adverts by subCategoryId
      const categoryAdverts = adverts.filter(
        (ad) => ad.subCategoryId === category.id
      );

      // Count active adverts
      const activeCount = categoryAdverts.filter((ad) => {
        const startDate = ad.startDate?.seconds
          ? dayjs.unix(ad.startDate.seconds)
          : null;
        const endDate = ad.endDate?.seconds
          ? dayjs.unix(ad.endDate.seconds)
          : null;

        return (
          (startDate && today.isSame(startDate)) ||
          (today.isAfter(startDate) && (!endDate || today.isBefore(endDate)))
        );
      }).length;

      // Count future adverts
      const futureCount = categoryAdverts.filter((ad) => {
        const startDate = ad.startDate?.seconds
          ? dayjs.unix(ad.startDate.seconds)
          : null;
        return startDate && today.isBefore(startDate);
      }).length;

      return {
        ...category,
        activeCount,
        futureCount,
        productCount,
      };
    });

    const categoriesWithCounts = await Promise.all(categoryPromises);
    return categoriesWithCounts;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Error fetching categories.');
  }
}

/**
 * Fetches the category or subcategory name based on category ID.
 * @param {string} catId - The category or subcategory ID to fetch.
 * @returns {Promise<string>} - The name of the category or subcategory.
 */
export const fetchCategoryOrSubcategoryName = async (catId) => {
  try {
    const categoryDocRef = doc(db, `marketplaceCategories`, catId);
    const categoryDoc = await getDoc(categoryDocRef);

    if (categoryDoc.exists()) {
      return categoryDoc.data().name || 'Unknown Category';
    } else {
      console.warn(`Category/Subcategory with ID ${catId} does not exist.`);
      return 'Unknown Category';
    }
  } catch (error) {
    console.error('Error fetching category/subcategory name:', error);
    throw new Error('Failed to fetch category or subcategory name.');
  }
};
